import { io, type Socket } from "socket.io-client";
import { useAuthStore } from "../stores/auth-store";

let socket: Socket | null = null;
let baseUrl = "";

const log = (...args: unknown[]) =>
  console.log("%c[realtime]", "color:#8b5cf6;font-weight:600", ...args);
const warn = (...args: unknown[]) =>
  console.warn("%c[realtime]", "color:#f59e0b;font-weight:600", ...args);
const err = (...args: unknown[]) =>
  console.error("%c[realtime]", "color:#ef4444;font-weight:600", ...args);

export function setRealtimeBaseUrl(url: string) {
  baseUrl = url;
  log("base url set:", url);
}

export function connectRealtime(): Socket {
  if (socket?.connected) {
    log("connect() skipped — already connected:", socket.id);
    return socket;
  }
  if (socket) {
    log("connect() reopening existing socket");
    socket.connect();
    return socket;
  }

  const url = `${baseUrl.replace(/\/+$/, "")}/realtime`;
  log("creating socket →", url);

  socket = io(url, {
    auth: (cb) => {
      const { user, deviceId, token } = useAuthStore.getState();
      const payload = {
        userId: user?.userId ?? "",
        deviceId: deviceId ?? "",
        token: token ?? "",
      };
      log("auth handshake", {
        userId: payload.userId,
        deviceId: payload.deviceId,
        hasToken: !!payload.token,
      });
      cb(payload);
    },
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  });

  socket.on("connect", () => {
    log(
      "✔ connected:",
      socket?.id,
      "transport:",
      socket?.io.engine.transport.name,
    );
  });

  socket.on("connect_error", (e) => {
    warn("✖ connect error:", e.message);
    if (e.message === "unauthorized") {
      warn("unauthorized — clearing session");
      useAuthStore.getState().clearSession();
    }
  });

  socket.on("disconnect", (reason) => {
    warn("✖ disconnected:", reason);
  });

  socket.on("error", (data) => {
    err("server error:", data);
  });

  socket.io.on("reconnect_attempt", (n) => log("reconnect attempt #", n));
  socket.io.on("reconnect", (n) => log("✔ reconnected after", n, "tries"));
  socket.io.on("reconnect_error", (e) => warn("reconnect error:", e.message));
  socket.io.on("reconnect_failed", () => err("reconnect failed — giving up"));

  socket.onAny((event, ...args) => {
    log("◀ event", event, args);
  });

  return socket;
}

export function disconnectRealtime() {
  if (!socket) return;
  log("disconnect() called");
  socket.disconnect();
  socket = null;
}

export function getRealtimeSocket(): Socket | null {
  return socket;
}
